import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** If set, user must type this exact phrase before the confirm button enables. */
  confirmPhrase?: string;
  danger?: boolean;
}

type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;

const ConfirmDialogContext = createContext<ConfirmFn | null>(null);

interface DialogState {
  open: boolean;
  message: string;
  options: ConfirmOptions;
}

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DialogState>({
    open: false,
    message: '',
    options: {},
  });
  const [typed, setTyped] = useState('');
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((message, options = {}) => {
    setTyped('');
    setState({ open: true, message, options });
    return new Promise<boolean>(resolve => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleClose = (confirmed: boolean) => {
    setState(s => ({ ...s, open: false }));
    resolveRef.current?.(confirmed);
    resolveRef.current = null;
  };

  const { title, confirmLabel = 'Confirm', cancelLabel = 'Cancel', confirmPhrase, danger } =
    state.options;
  const phraseMatches = !confirmPhrase || typed === confirmPhrase;

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <Dialog
        open={state.open}
        onClose={() => handleClose(false)}
        maxWidth="xs"
        fullWidth
      >
        {title && <DialogTitle>{title}</DialogTitle>}
        <DialogContent>
          <DialogContentText>{state.message}</DialogContentText>
          {confirmPhrase && (
            <TextField
              autoFocus
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label={`Type "${confirmPhrase}" to confirm`}
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && phraseMatches && handleClose(true)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose(false)}>{cancelLabel}</Button>
          <Button
            onClick={() => handleClose(true)}
            color={danger ? 'error' : 'primary'}
            variant="contained"
            disabled={!phraseMatches}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmDialogContext);
  if (!fn) throw new Error('useConfirm must be used within ConfirmDialogProvider');
  return fn;
}
